import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CircleAlertIcon } from 'lucide-react';
import { usePortalAuth } from '../../context/PortalAuth';
import { orgName } from '../../context/PortalOrg';
import { usePortalQuery } from '../../hooks/usePortalQuery';
import { listConversations, listInvoices, listOrgDeliverables, listProjects, listTasks, type DeliverableGroup } from '../../lib/portalApi';
import { can } from '../../lib/portalPermissions';
import { PRODUCTS } from '../../data/portal';
import { ProductTag } from '../../components/portal/ProductTag';
import { StatusBadge } from '../../components/portal/StatusBadge';
import { EmptyState, QueryState } from '../../components/portal/QueryState';
import { daysUntil, formatDate, formatMoney, formatRelative, TASK_STATUS_LABELS } from '../../utils/portalFormat';
import type { ConversationSummary, Invoice, Project, Task } from '../../types/portal';
interface Overview {
  projects: Project[];
  myTasks: (Task & {
    orgId: string;
    projectName: string;
  })[];
  deliverables: (DeliverableGroup & {
    orgId: string;
  })[];
  unread: (ConversationSummary & {
    orgId: string;
  })[];
  invoices: Invoice[];
}

/**
 * The unified dashboard: every engagement across every organisation and brand
 * the person belongs to. Each panel only draws on organisations where their
 * role permits it, so a viewer never sees billing and a member only sees the
 * invoices addressed to them.
 */
export function PortalDashboard() {
  const {
    user,
    memberships
  } = usePortalAuth();
  const [product, setProduct] = React.useState<string>('all');
  const {
    data,
    loading,
    error
  } = usePortalQuery<Overview>(async () => {
    const perOrg = await Promise.all(memberships.map(async m => {
      const projects = (await listProjects(m.orgId, {
        limit: 100
      })).data;
      const tasks = await Promise.all(projects.filter(p => p.status === 'active').map(async p => (await listTasks(m.orgId, p.id)).map(t => ({
        ...t,
        orgId: m.orgId,
        projectName: p.name
      }))));
      const deliverables = (await listOrgDeliverables(m.orgId)).map(d => ({
        ...d,
        orgId: m.orgId
      }));
      const unread = can(m.role, 'messages.view') ? (await listConversations(m.orgId)).filter(c => c.unreadCount > 0).map(c => ({
        ...c,
        orgId: m.orgId
      })) : [];
      const invoices = can(m.role, 'invoices.viewAll') || can(m.role, 'invoices.viewOwn') ? (await listInvoices(m.orgId)).data : [];
      return {
        projects,
        tasks: tasks.flat(),
        deliverables,
        unread,
        invoices
      };
    }));
    return {
      projects: perOrg.flatMap(o => o.projects),
      myTasks: perOrg.flatMap(o => o.tasks).filter(t => t.assignedTo === user?.id && t.status !== 'done').sort((a, b) => (a.dueDate ?? '9').localeCompare(b.dueDate ?? '9')),
      deliverables: perOrg.flatMap(o => o.deliverables).sort((a, b) => b.latest.createdAt.localeCompare(a.latest.createdAt)).slice(0, 4),
      unread: perOrg.flatMap(o => o.unread),
      invoices: perOrg.flatMap(o => o.invoices).filter(i => i.status === 'sent' || i.status === 'overdue')
    };
  }, [memberships.map(m => `${m.orgId}:${m.role}`).join(','), user?.id]);
  const products = PRODUCTS.filter(p => memberships.some(m => m.productIds.includes(p.id)));
  const shown = (data?.projects ?? []).filter(p => product === 'all' || p.productId === product);
  const active = shown.filter(p => p.status === 'active' || p.status === 'paused');
  const finished = shown.filter(p => p.status === 'completed');
  return <div data-id="element-2852" data-mp="ee9v742za97t">
      <header className="border-b border-line pb-7" data-id="element-2853" data-mp="eeib74uedun8">
        <p className="text-[13px] text-muted" data-id="element-2854" data-mp="t2d3ik4htwzs">
          {memberships.length} organisation{memberships.length === 1 ? '' : 's'}, {products.map(p => p.name).join(' and ')}
        </p>
        <h1 className="mt-2 font-display text-[30px] font-light leading-tight text-ink sm:text-[36px]" data-id="element-2855" data-mp="lk8syjjf2a6">Good to see you, {user?.firstName}.</h1>
      </header>

      <QueryState loading={loading} error={error} rows={4} data-id="element-2856" data-mp="3rrbvfny3vws8">
        {data && <div className="mt-8 grid gap-10 xl:grid-cols-[1fr_340px]" data-id="element-2857" data-mp="2v1hyjq03i11z">
            <div className="min-w-0 space-y-10" data-id="element-2858" data-mp="2z1jy9tm9skem">
              {/* Needs attention: the one thing to act on first */}
              {(data.invoices.some(i => i.status === 'overdue') || data.myTasks.length > 0) && <section aria-labelledby="attention" className="rounded-md border border-line bg-panel" data-id="element-2859" data-mp="38swq4l82jakn">
                  <h2 id="attention" className="border-b border-line px-5 py-4 text-[15px] font-medium text-ink" data-id="element-2860" data-mp="3dpwoe9yof917">
                    Waiting on you
                  </h2>
                  <ul className="divide-y divide-line" data-id="element-2861" data-mp="3mr4jkj5wbtr2">
                    {data.invoices.filter(i => i.status === 'overdue').map(i => <li key={i.id} data-id="element-2862" data-mp="1sdv66ga8bo3j">
                          <Link to={`/portal/orgs/${i.orgId}/invoices?invoice=${i.id}`} className="flex items-center gap-4 px-5 py-4 transition-colors duration-150 hover:bg-charcoal" data-id="element-2863" data-mp="1sewqlbd4xmxx">
                            <CircleAlertIcon className="h-5 w-5 shrink-0 text-[#FF8B7A]" aria-hidden="true" data-id="element-2864" data-mp="1pu8jro7csk0h" />
                            <span className="min-w-0 flex-1" data-id="element-2865" data-mp="1l1owu0ms0cj0">
                              <span className="block text-[15px] text-ink" data-id="element-2866" data-mp="1g3440m9usv4l">
                                {i.invoiceNumber} is overdue, {formatMoney(i.totalCents, i.currency)}
                              </span>
                              <span className="block text-[13px] text-muted" data-id="element-2867" data-mp="28sb61v8io2ul">
                                {orgName(i.orgId)}, due {formatDate(i.dueDate)}
                              </span>
                            </span>
                            <ArrowRightIcon className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" data-id="element-2868" data-mp="1ysai1vzpcyk2" />
                          </Link>
                        </li>)}
                    {data.myTasks.slice(0, 4).map(t => {
                const days = t.dueDate ? daysUntil(t.dueDate) : null;
                return <li key={t.id} data-id="element-2869" data-mp="ktquivndbeta">
                          <Link to={`/portal/orgs/${t.orgId}/projects/${t.projectId}?tab=tasks`} className="flex items-center gap-4 px-5 py-4 transition-colors duration-150 hover:bg-charcoal" data-id="element-2870" data-mp="kuthwtlhez4g">
                            <span className="min-w-0 flex-1" data-id="element-2871" data-mp="avb46uw609ku">
                              <span className="block text-[15px] text-ink" data-id="element-2872" data-mp="funyg0pd8ixg">{t.title}</span>
                              <span className="block text-[13px] text-muted" data-id="element-2873" data-mp="242cg34m7svc">
                                {t.projectName}, {TASK_STATUS_LABELS[t.status].toLowerCase()}
                              </span>
                            </span>
                            {days !== null && <span className={`shrink-0 text-[13px] ${days < 0 ? 'text-[#FFA192]' : days <= 3 ? 'text-[#F7CF86]' : 'text-muted'}`} data-id="element-2874" data-mp="3ta3k01pdg4c8">
                                {days < 0 ? `${Math.abs(days)} days late` : days === 0 ? 'Due today' : `Due in ${days} days`}
                              </span>}
                          </Link>
                        </li>;
              })}
                  </ul>
                </section>}

              <section aria-labelledby="engagements" data-id="element-2875" data-mp="39o1ux7cv65zk">
                <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-2876" data-mp="39plgndhah98q">
                  <h2 id="engagements" className="font-display text-[22px] text-ink" data-id="element-2877" data-mp="34ofmrcbw7fxm">
                    Engagements
                  </h2>
                  {products.length > 1 && <div role="group" aria-label="Filter by brand" className="flex rounded-sm border border-line p-0.5" data-id="element-2878" data-mp="3embvxyl9s0t3">
                      {[{
                  id: 'all',
                  name: 'All'
                }, ...products].map(p => <button key={p.id} type="button" aria-pressed={product === p.id} onClick={() => setProduct(p.id)} className={`rounded-[7px] px-3 py-1.5 text-[13px] transition-colors duration-150 ${product === p.id ? 'bg-panel text-ink' : 'text-muted hover:text-ink'}`} data-id="element-2879" data-mp="3oe99vd3nx6vi">
                          {p.name}
                        </button>)}
                    </div>}
                </div>

                {active.length === 0 ? <div className="mt-5" data-id="element-2880" data-mp="1mimgg9j612kk">
                    <EmptyState title="No active engagements" body="Projects you are assigned to appear here as soon as they start." data-id="element-2881" data-mp="oxgnb57shyqg" />
                  </div> : <ul className="mt-5 divide-y divide-line border-y border-line" data-id="element-2882" data-mp="yv4mc7cfhsef">
                    {active.map(p => <li key={p.id} data-id="element-2883" data-mp="13m4fr2mc9sc6">
                        <Link to={`/portal/orgs/${p.orgId}/projects/${p.id}`} className="group grid gap-2 py-5 sm:grid-cols-[1fr_auto] sm:items-center sm:gap-6" data-id="element-2884" data-mp="13n5upi6h6yz6">
                          <span className="min-w-0" data-id="element-2885" data-mp="b4s7ecirmvec">
                            <span className="flex flex-wrap items-center gap-x-4 gap-y-1" data-id="element-2886" data-mp="b378ybtbysb7">
                              <ProductTag productId={p.productId} data-id="element-2887" data-mp="3jy9pbejg9zv" />
                              <span className="text-[12px] text-muted" data-id="element-2888" data-mp="3utn821dcutrs">{orgName(p.orgId)}</span>
                            </span>
                            <span className="mt-1.5 block text-[17px] text-ink transition-colors duration-150 group-hover:text-royal" data-id="element-2889" data-mp="3pvbzdtysqpb5">{p.name}</span>
                          </span>
                          <span className="flex items-center gap-4 sm:justify-end" data-id="element-2890" data-mp="3gu45do3bcamv">
                            <span className="text-[13px] text-muted" data-id="element-2891" data-mp="3c12lf1o91kwf">Ends {formatDate(p.endDate)}</span>
                            <StatusBadge kind="project" status={p.status} data-id="element-2892" data-mp="373j41sw98s09" />
                          </span>
                        </Link>
                      </li>)}
                  </ul>}

                {finished.length > 0 && <p className="mt-4 text-[14px] text-muted" data-id="element-2893" data-mp="2t2q9j85ydwx5">
                    {finished.length} completed engagement{finished.length === 1 ? '' : 's'}, with every deliverable still available in each organisation’s projects.
                  </p>}
              </section>
            </div>

            <aside className="space-y-8" data-id="element-2894" data-mp="1kq973y9u9oh">
              <section aria-labelledby="recent-files" data-id="element-2895" data-mp="1mjd4bhi2fss">
                <h2 id="recent-files" className="text-[15px] font-medium text-ink" data-id="element-2896" data-mp="3srswvlewhxty">
                  Latest deliverables
                </h2>
                {data.deliverables.length === 0 ? <p className="mt-3 text-[14px] text-muted" data-id="element-2897" data-mp="302vny8wkxddv">Nothing has been shared yet.</p> : <ul className="mt-3 space-y-1" data-id="element-2898" data-mp="3e2myfznw0bn4">
                    {data.deliverables.map(d => <li key={d.latest.id} data-id="element-2899" data-mp="39yp03r6dz4v4">
                        <Link to={`/portal/orgs/${d.orgId}/projects/${d.project.id}?tab=deliverables`} className="block rounded-sm px-3 py-2.5 -mx-3 transition-colors duration-150 hover:bg-panel" data-id="element-2900" data-mp="tbmhrsdats5y">
                          <span className="block text-[14px] text-ink" data-id="element-2901" data-mp="wtk0dcr72zl8">
                            {d.latest.name}
                            {d.latest.version > 1 && <span className="text-muted" data-id="element-2902" data-mp="11tokrdv84egq">, v{d.latest.version}</span>}
                          </span>
                          <span className="block text-[12px] text-muted" data-id="element-2903" data-mp="mwxj0xy2uqny">
                            {d.project.name}, {formatRelative(d.latest.createdAt)}
                          </span>
                        </Link>
                      </li>)}
                  </ul>}
              </section>

              {data.unread.length > 0 && <section aria-labelledby="unread" data-id="element-2904" data-mp="15t74i6bb6jhz">
                  <h2 id="unread" className="text-[15px] font-medium text-ink" data-id="element-2905" data-mp="15se19fbk6c4l">
                    Unread messages
                  </h2>
                  <ul className="mt-3 space-y-1" data-id="element-2906" data-mp="1yinukxbghob1">
                    {data.unread.map(c => <li key={c.id} data-id="element-2907" data-mp="239w4dugp7boq">
                        <Link to={`/portal/orgs/${c.orgId}/messages?c=${c.id}`} className="-mx-3 flex items-center gap-3 rounded-sm px-3 py-2.5 transition-colors duration-150 hover:bg-panel" data-id="element-2908" data-mp="23axg815jg27i">
                          <span className="min-w-0 flex-1 truncate text-[14px] text-ink" data-id="element-2909" data-mp="287b5xztfdbz3">{c.subject}</span>
                          <span className="rounded-full bg-gold px-2 text-[12px] font-bold text-charcoal" data-id="element-2910" data-mp="24711wsp70vmv">{c.unreadCount}</span>
                        </Link>
                      </li>)}
                  </ul>
                </section>}

              {data.invoices.length > 0 && <section aria-labelledby="open-invoices" data-id="element-2911" data-mp="1pe78s4h00gzq">
                  <h2 id="open-invoices" className="text-[15px] font-medium text-ink" data-id="element-2912" data-mp="1khqnrj53iiqv">
                    Open invoices
                  </h2>
                  <ul className="mt-3 space-y-1" data-id="element-2913" data-mp="1bg98u43fcn3r">
                    {data.invoices.map(i => <li key={i.id} data-id="element-2914" data-mp="16h61gczz24sm">
                        <Link to={`/portal/orgs/${i.orgId}/invoices?invoice=${i.id}`} className="-mx-3 flex items-center justify-between gap-3 rounded-sm px-3 py-2.5 transition-colors duration-150 hover:bg-panel" data-id="element-2915" data-mp="16i7gesk3zbfm">
                          <span data-id="element-2916" data-mp="3n21cejwzqnp5">
                            <span className="block text-[14px] text-ink" data-id="element-2917" data-mp="3n5fhw10ybd0t">{formatMoney(i.totalCents, i.currency)}</span>
                            <span className="block text-[12px] text-muted" data-id="element-2918" data-mp="3w40tfc809rs3">{i.invoiceNumber}</span>
                          </span>
                          <StatusBadge kind="invoice" status={i.status} data-id="element-2919" data-mp="2zf2ynu099tux" />
                        </Link>
                      </li>)}
                  </ul>
                </section>}
            </aside>
          </div>}
      </QueryState>
    </div>;
}